import { StatusType } from '../shared/types/inventory';
import { getStatusInfo } from '../shared/utils/inventoryCalculations';
import { AlertTriangle, AlertOctagon, TrendingDown, ShieldAlert, ArrowUpCircle, CheckCircle } from 'lucide-react';

interface StatusCardsProps {
  counts: Record<StatusType, number>;
  activeStatus: StatusType | null;
  onStatusClick: (status: StatusType) => void;
}

const STATUS_ORDER: StatusType[] = ['ruptura', 'iminente', 'minimo', 'critica', 'excesso', 'saudavel'];

const STATUS_ICONS: Record<StatusType, React.ReactNode> = {
  ruptura: <AlertOctagon className="w-5 h-5" />,
  iminente: <AlertTriangle className="w-5 h-5" />,
  minimo: <TrendingDown className="w-5 h-5" />,
  critica: <ShieldAlert className="w-5 h-5" />,
  excesso: <ArrowUpCircle className="w-5 h-5" />,
  saudavel: <CheckCircle className="w-5 h-5" />,
};

const STATUS_RING_COLORS: Record<StatusType, string> = {
  ruptura: 'ring-red-500',
  iminente: 'ring-orange-500',
  minimo: 'ring-yellow-500',
  critica: 'ring-cyan-500',
  excesso: 'ring-blue-500',
  saudavel: 'ring-green-500',
};

const STATUS_ICON_COLORS: Record<StatusType, string> = {
  ruptura: 'text-red-600',
  iminente: 'text-orange-600',
  minimo: 'text-yellow-600',
  critica: 'text-cyan-600',
  excesso: 'text-blue-600',
  saudavel: 'text-green-600',
};

export const StatusCards = ({ counts, activeStatus, onStatusClick }: StatusCardsProps) => {
  const total = Object.values(counts).reduce((sum, c) => sum + c, 0);

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3 mb-6">
      <div
        className={`bg-white rounded-xl border-2 p-4 transition-all cursor-pointer shadow-sm hover:shadow-md ${
          activeStatus === null
            ? 'border-teal-500 ring-2 ring-teal-200'
            : 'border-gray-200 hover:border-gray-300'
        }`}
        onClick={() => {
          if (activeStatus !== null) onStatusClick(activeStatus);
        }}
      >
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Total</p>
        <p className="text-2xl font-bold text-gray-900">{total}</p>
      </div>

      {STATUS_ORDER.map((status) => {
        const info = getStatusInfo(status);
        const isActive = activeStatus === status;
        const count = counts[status];

        return (
          <div
            key={status}
            onClick={() => onStatusClick(status)}
            className={`bg-white rounded-xl border-2 p-4 transition-all cursor-pointer shadow-sm hover:shadow-md ${
              isActive
                ? `border-current ${STATUS_RING_COLORS[status]} ring-2`
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider truncate pr-1">
                {info.label}
              </p>
              <span className={STATUS_ICON_COLORS[status]}>
                {STATUS_ICONS[status]}
              </span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{count}</p>
          </div>
        );
      })}
    </div>
  );
};
